import { IonCol, IonGrid, IonRow } from '@ionic/react';
import equal from 'deep-equal';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { BooleanParam, createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import Checkbox from '@components/fields/Checkbox';
import Select from '@components/fields/Select';
import SearchInput from '@components/fieldsNative/SearchInput';
import Form from '@components/forms/Form';
import { FocusAreaEnum, GoalTemplateKindEnum } from '@typing/Generated';
import { optionsForFocusArea } from '@utils/optionUtils';

type FormData = {
  archived?: boolean | null;
  focusArea?: FocusAreaEnum | null;
  kind?: GoalTemplateKindEnum;
  searchTerm?: string | null;
  usedInJourney?: boolean | null;
};

export type Filters = FormData;

type Props = {
  filters: Filters;
  journeyId?: string;
  onChange: (filterValues: FormData) => void;
};

const GoalTableFilters = ({ filters, journeyId, onChange }: Props) => {
  const intl = useIntl();

  const focusAreaParam = createEnumParam<FocusAreaEnum>(Object.values(FocusAreaEnum));
  const kindParam = createEnumParam<GoalTemplateKindEnum>(Object.values(GoalTemplateKindEnum));

  const [query, setQuery] = useQueryParams({
    archived: withDefault({ ...BooleanParam, urlName: 'goalTemplateArchived' }, filters.archived),
    focusArea: withDefault({ ...focusAreaParam, urlName: 'goalTemplateFocusArea' }, filters.focusArea),
    kind: withDefault({ ...kindParam, urlName: 'goalTemplateKind' }, filters.kind),
    searchTerm: withDefault({ ...StringParam, urlName: 'goalTemplateSearch' }, filters.searchTerm),
    usedInJourney: withDefault({ ...BooleanParam, urlName: 'goalTemplateUsedInJourney' }, filters.usedInJourney)
  });

  const initialValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const archived = form.watch('archived');
  const focusArea = form.watch('focusArea');
  const kind = form.watch('kind');
  const searchTerm = form.watch('searchTerm');
  const usedInJourney = form.watch('usedInJourney');

  useEffect(() => {
    const values = { archived, focusArea, kind, searchTerm, usedInJourney };
    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
  }, [filters, searchTerm, onChange, setQuery, kind, usedInJourney, archived, focusArea]);

  const focusAreaOptions = optionsForFocusArea(intl);

  const kindOptions = useMemo(
    () =>
      Object.values(GoalTemplateKindEnum).map(option => ({
        label: intl.formatMessage({ id: `models.goalTemplate.kind.${option}` }),
        value: option
      })),
    [intl]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form hookForm={form} onSubmit={() => {}}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol>
            <SearchInput
              error={form.formState.errors.searchTerm?.message}
              initialValue={initialValues.searchTerm}
              label={intl.formatMessage({ id: 'forms.goalTemplateFilters.searchTermLabel' })}
              name="searchTerm"
              setValue={v => form.setValue('searchTerm', v)}
            />
          </IonCol>
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.focusArea}
              label={intl.formatMessage({ id: 'forms.goalTemplateFilters.focusAreaLabel' })}
              name="focusArea"
              options={focusAreaOptions}
            />
          </IonCol>
          {journeyId && (
            <IonCol className="ion-margin-top" size="auto">
              <div>
                <Checkbox<FormData>
                  control={form.control}
                  error={form.formState.errors.usedInJourney}
                  name="usedInJourney"
                >
                  <>
                    <FormattedMessage id="forms.goalTemplateFilters.usedInJourneyLabel" />
                  </>
                </Checkbox>
              </div>
            </IonCol>
          )}
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.kind}
              label={intl.formatMessage({ id: 'forms.goalTemplateFilters.kindLabel' })}
              name="kind"
              options={kindOptions}
            />
          </IonCol>
          <IonCol className="ion-margin-top" size="auto">
            <div>
              <Checkbox<FormData> control={form.control} error={form.formState.errors.archived} name="archived">
                <>
                  <FormattedMessage id="forms.worksheetTemplateFilters.archivedLabel" />
                </>
              </Checkbox>
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default GoalTableFilters;
